import { ref, computed, readonly } from 'vue'

import { defineStore, storeToRefs } from 'pinia'

import { useCommonStore } from '@/stores/CommonStore'
import { EmptyCard } from '@/pages/infoLibrary/config/library.constants'
import {
  LibraryFilterCardsBy,
  LibrarySortCardsBy
} from '@/pages/infoLibrary/config/library.enums'
import {
  convertReadonlyCardToRaw,
  createCard,
  createTab,
  filterCards,
  sortCards
} from '../config/library.utils'
import type { ITemplateCard } from '@/models/interfaces'
import type { Card, Tab } from '@/pages/infoLibrary/config/library.types'

/**
 * Pure InfoLibrary State Store
 *
 * This store is a pure state container with synchronous mutations only.
 * All async operations and API calls are handled by composables
 * (useLibraryList, useLibraryEntry, useLibraryActions).
 *
 * Responsibilities:
 * - Hold InfoLibrary feature state (tabs, cards, form state)
 * - Provide synchronous state mutations
 * - Expose readonly state to components
 *
 * Does NOT contain:
 * - Direct API calls (httpClient)
 * - Repository calls (async operations)
 * - Cache management
 * - Error logging
 */
export const useLibraryStore = defineStore('infoLibrary', () => {
  const { loading, fullUserInfo } = storeToRefs(useCommonStore())

  // ===== STATE =====

  // Tab state
  const tabs = ref<Tab[]>([])
  const selectedTabId = ref<number>(0)
  const addTabErrorMessage = ref<string | null>(null)
  const tabForEdit = ref<Tab>(createTab())
  const editTabErrorMessage = ref<string | null>(null)
  const deleteTabErrorMessage = ref<string | null>(null)

  // Card state
  const selectedCardIdForUpdate = ref<number | null>(null)
  const addCardErrorMessage = ref<string | null>(null)
  const showEditCard = ref<boolean>(false)
  const cardForEdit = ref<Card>(createCard())
  const editCardErrorMessage = ref<string | null>(null)
  const deleteCardErrorMessage = ref<string | null>(null)
  const cards = ref<Card[]>([])
  const selectedCard = ref<Card | null>(null)
  const isFetchingCards = ref<boolean>(false)
  const isFetchingDetails = ref<boolean>(false)

  // Search/filter/sort state
  const searchTerm = ref<string | null>(null)
  const filterCardsBy = ref<LibraryFilterCardsBy>(LibraryFilterCardsBy.Default)
  const sortCardsBy = ref<LibrarySortCardsBy>(LibrarySortCardsBy.Default)

  // Template state
  const currentTemplate = ref<ITemplateCard | null>({ ...EmptyCard })

  // ===== COMPUTED =====

  const selectedCardId = computed<number | null>(
    () => selectedCard.value?.itemId ?? null
  )

  const canDeleteTab = computed(() => {
    return !loading.value && selectedTabId.value !== 0
  })

  const canEditCard = computed(() => {
    return (
      !loading.value &&
      selectedCardId.value != null &&
      selectedCardId.value !== 0
    )
  })

  const canDeleteCard = computed(() => {
    return (
      !loading.value &&
      selectedCardId.value != null &&
      selectedCardId.value !== 0
    )
  })

  const getSelectedTab = computed(() => {
    return tabs.value.find((t) => t.itemId === selectedTabId.value)
  })

  const getSelectedCard = computed(() => {
    return (
      selectedCard.value ??
      cards.value.find((c) => c.itemId === selectedCardId.value)
    )
  })

  const getTotalCardLength = computed(() => {
    return cards.value.length
  })

  const getPersonalCardsLength = computed(() => {
    return cards.value.filter((c) => c.userId === fullUserInfo.value.userId)
      .length
  })

  const tabCards = computed(() => {
    if (isFetchingCards.value) {
      return createPlaceholderCards()
    }

    const cardsForSelectedTab = selectedTabId.value
      ? cards.value.filter((card) => card.tabId === selectedTabId.value)
      : cards.value

    const filteredCards = filterCards(
      cardsForSelectedTab,
      searchTerm.value,
      filterCardsBy.value,
      fullUserInfo.value.userId ?? 0
    )

    return sortCards(filteredCards, sortCardsBy.value).map((c) =>
      convertReadonlyCardToRaw(c)
    )
  })

  // ===== SYNCHRONOUS MUTATIONS =====
  function setTabs(newTabs: Tab[]): void {
    tabs.value = newTabs
  }

  function setSearchTerm(term: string | null): void {
    searchTerm.value = term
  }

  function setFilterCardsBy(filter: LibraryFilterCardsBy): void {
    filterCardsBy.value = filter
  }

  function setSortCardsBy(order: LibrarySortCardsBy): void {
    sortCardsBy.value = order
  }

  function setAddCardErrorMessage(message: string | null): void {
    addCardErrorMessage.value = message
  }

  function setShowEditCard(show: boolean): void {
    showEditCard.value = show
  }

  function setCardForEdit(card: Card | null): void {
    cardForEdit.value = card ?? createCard()
  }

  function setEditCardErrorMessage(message: string | null): void {
    editCardErrorMessage.value = message
  }

  function setAddTabErrorMessage(message: string | null): void {
    addTabErrorMessage.value = message
  }

  function setTabForEdit(tab: Tab | null): void {
    tabForEdit.value = tab ?? createTab()
  }

  function setEditTabErrorMessage(message: string | null): void {
    editTabErrorMessage.value = message
  }

  function setSelectedCardIdForUpdate(cardId: number | null): void {
    selectedCardIdForUpdate.value = cardId
  }

  function setDeleteCardErrorMessage(message: string | null): void {
    deleteCardErrorMessage.value = message
  }

  function setSelectedTabId(tabId: number): void {
    selectedTabId.value = tabId
  }

  function setDeleteTabErrorMessage(message: string | null): void {
    deleteTabErrorMessage.value = message
  }

  function setCurrentTemplate(template: ITemplateCard | null): void {
    currentTemplate.value = template
  }

  function setSelectedCard(card: Card | null): void {
    selectedCard.value = card
  }

  function setIsFetchingDetails(value: boolean): void {
    isFetchingDetails.value = value
  }

  function setIsFetchingCards(value: boolean): void {
    isFetchingCards.value = value
  }

  function replaceCard(card: Card): void {
    cards.value = cards.value.map((existingCard) =>
      existingCard.itemId === card.itemId ? card : existingCard
    )
    if (selectedCard.value?.itemId === card.itemId) {
      selectedCard.value = card
    }
  }

  // ===== SYNC HELPERS =====
  function createPlaceholderCards(): Card[] {
    return Array.from({ length: 5 }, () => createCard())
  }

  function getTabName(tabName: string): string {
    return tabName ?? fullUserInfo.value.coparentFirstName
  }

  function getTabId(tabName: string): string {
    return 'tab' + getTabName(tabName).replace(' ', '')
  }

  function checkTitleForDuplicate(title: string, itemId: number): boolean {
    const duplicate = cards.value.find(
      (x) => x.shared === false && x.itemId !== itemId && x.title === title
    )
    return !!duplicate
  }

  function checkForDuplicate(text: string, itemId: number): boolean {
    const duplicate = tabs.value.find(
      (x) => x.itemId !== itemId && x.name === text
    )
    return !!duplicate
  }

  function setCards(newCards: Card[]): void {
    cards.value = newCards
  }

  function reset(): void {
    tabs.value = []
    selectedTabId.value = 0
    addTabErrorMessage.value = null
    tabForEdit.value = createTab()
    editTabErrorMessage.value = null
    deleteTabErrorMessage.value = null
    selectedCardIdForUpdate.value = null
    addCardErrorMessage.value = null
    showEditCard.value = false
    cardForEdit.value = createCard()
    editCardErrorMessage.value = null
    deleteCardErrorMessage.value = null
    cards.value = []
    selectedCard.value = null
    isFetchingCards.value = false
    isFetchingDetails.value = false
    searchTerm.value = null
    filterCardsBy.value = LibraryFilterCardsBy.Default
    sortCardsBy.value = LibrarySortCardsBy.Default
    currentTemplate.value = { ...EmptyCard }
  }

  return {
    // === Readonly state ===
    tabs: readonly(tabs),
    selectedTabId: readonly(selectedTabId),
    addTabErrorMessage: readonly(addTabErrorMessage),
    tabForEdit: readonly(tabForEdit),
    editTabErrorMessage: readonly(editTabErrorMessage),
    deleteTabErrorMessage: readonly(deleteTabErrorMessage),
    selectedCardId,
    selectedCardIdForUpdate: readonly(selectedCardIdForUpdate),
    addCardErrorMessage: readonly(addCardErrorMessage),
    showEditCard: readonly(showEditCard),
    cardForEdit: readonly(cardForEdit),
    editCardErrorMessage: readonly(editCardErrorMessage),
    deleteCardErrorMessage: readonly(deleteCardErrorMessage),
    cards: readonly(cards),
    searchTerm: readonly(searchTerm),
    filterCardsBy: readonly(filterCardsBy),
    sortCardsBy: readonly(sortCardsBy),
    tabCards: readonly(tabCards),
    currentTemplate: readonly(currentTemplate),
    selectedCard: readonly(selectedCard),
    isFetchingDetails: readonly(isFetchingDetails),

    // === Computed ===
    canDeleteTab,
    canEditCard,
    canDeleteCard,
    getSelectedTab,
    getSelectedCard,
    getTotalCardLength,
    getPersonalCardsLength,

    // === Sync Mutations ===
    setTabs,
    setSearchTerm,
    setFilterCardsBy,
    setSortCardsBy,
    setAddCardErrorMessage,
    setShowEditCard,
    setCardForEdit,
    setEditCardErrorMessage,
    setAddTabErrorMessage,
    setTabForEdit,
    setEditTabErrorMessage,
    setSelectedCardIdForUpdate,
    setDeleteCardErrorMessage,
    setSelectedTabId,
    setDeleteTabErrorMessage,
    setCurrentTemplate,
    setSelectedCard,
    setIsFetchingDetails,
    setIsFetchingCards,
    replaceCard,

    // === Sync Helpers ===
    getTabName,
    getTabId,
    checkTitleForDuplicate,
    checkForDuplicate,
    setCards,
    reset
  }
})
